// src/components/Procurement/NewPOForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import api from "@/lib/axios";
import { useAuth } from "@/context/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import usePoAttachmentWorkflow from "@/hooks/usePoAttachmentWorkflow";
import { useProcurementOperationalContext } from "@/hooks/useProcurementOperationalContext";
import { procurementContextKey } from "@/lib/procurementAssistantRequests";
import { PO_CURRENCIES, poExpectedScope, validatePoFinancialContext, poFinancialWritesReady,
  serializePoLine, previewPoTotals, formatPoMoney, draftPoFinancials, poFxReady,
  isPoCurrencySupported, poManualFxEntry, assertPoMoneyRange } from "@/lib/poFinancialForm";
import POExchangeRates from './POExchangeRates';
import POAttachmentPanel from "./POAttachmentPanel";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// PO_ATTACHMENTS_FORM_V1: isolate in-memory files when account or draft navigation changes.
export default function NewPOForm() {
  const { user } = useAuth();
  const { search } = useLocation();
  const operational = useProcurementOperationalContext(user, Boolean(user));
  if (operational.loading) return <p role="status">Loading selected company and unit…</p>;
  if (operational.error) return <p role="alert">{operational.error}</p>;
  const key = procurementContextKey(operational.expected);
  if (!key) return <p role="alert">Select a company and unit before opening a PO.</p>;
  return <>
    {operational.refreshing && <p role="status">Checking selected company and unit…</p>}
    <div hidden={operational.refreshing} inert={operational.refreshing ? '' : undefined}>
      <NewPOFinancialSession key={`${key}:${search}`} user={operational.expected} />
    </div>
  </>;
}

function NewPOFinancialSession({ user }) {
  const [context, setContext] = useState(null), [error, setError] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    (async () => {
      try {
        const { data } = await api.get('/purchase-orders/form-financial-context', { signal: abort.signal });
        const value = validatePoFinancialContext(data, poExpectedScope(user));
        if (!poFinancialWritesReady(value)) throw new Error('The PO form update is awaiting server activation. Refresh after activation.');
        if (!abort.signal.aborted) setContext(value);
      } catch (cause) {
        if (!abort.signal.aborted) setError(cause?.response?.data?.error || cause.message || 'PO currency and tax settings are unavailable.');
      }
    })();
    return () => abort.abort();
  }, [user]);
  if (error) return <p role="alert" className="rounded-lg bg-red-50 p-4 text-red-800">{error}</p>;
  if (!context) return <p role="status">Loading PO currency and tax settings…</p>;
  return <NewPOFormSession user={user} financialContext={context} />;
}

function NewPOFormSession({ user, financialContext }) {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useUserPermissions();
  const canViewDocuments = hasPermission("procurement.po.view");
  const canUploadDocuments = canViewDocuments && hasPermission("procurement.po.edit");
  const actionLock = useRef(false);
  const [draftAccessError, setDraftAccessError] = useState("");
  const [poNotes, setPoNotes] = useState("");

  const [bootLoading, setBootLoading] = useState(true);
  const [draftLoading, setDraftLoading] = useState(false);

  const [draftPoId, setDraftPoId] = useState(null);
  const [draftPoNumber, setDraftPoNumber] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [deliveryDate, setDeliveryDate] = useState("");

  const [vendorItems, setVendorItems] = useState([]);
  const [lineItems, setLineItems] = useState([]);

  const [planners, setPlanners] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [events, setEvents] = useState([]);

  const [selectedPlanner, setSelectedPlanner] = useState(null);
  const [selectedCostCentre, setSelectedCostCentre] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [transactionCurrency, setTransactionCurrency] = useState(financialContext.defaults.transaction_currency || '');
  const [quoteConfirmed, setQuoteConfirmed] = useState(false);
  const [savedFinancialHeader, setSavedFinancialHeader] = useState(null);
  const [fxEditing, setFxEditing] = useState(false);
  const [fxFields, setFxFields] = useState({rate_to_unit:'',rate_to_reporting:'',source_reference:'',as_of_date:''});
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const hasToastedRef = useRef(false);

  const prefillPlanRef = useRef(null);
  const prefillAppliedRef = useRef(false);
  const draftLoadedRef = useRef(false);

  const attachments = usePoAttachmentWorkflow({
    draftId: draftPoId,
    canView: canViewDocuments,
    canUpload: canUploadDocuments,
    onSaved: (saved) => {
      // A newly created draft must not trigger a form reload halfway through uploads.
      draftLoadedRef.current = true;
      prefillAppliedRef.current = true;
      setDraftPoId(saved.po_id);
      setDraftPoNumber(saved.po_number || null);
      setDraftStatus(saved.status);
    },
    onStatus: setDraftStatus,
    onFinancialVerified: (data) => { setSavedFinancialHeader(data.header); setFxEditing(false); },
  });

  const fmt = (n) => formatPoMoney(n, transactionCurrency, financialContext.locale);
  const makeNewLine = (item, quantity = 1) => ({ ...item,
    line_type: 'catalogue', description: item.description || item.item_name || item.item_code,
    quantity: String(quantity), unit_price: String(item.unit_price ?? ''),
    tax_rate: financialContext.defaults.line_tax_rate ?? '',
  });

  // ---------- helpers ----------
  const toArray = (x, key) => {
    if (Array.isArray(x)) return x;
    if (key && Array.isArray(x?.[key])) return x[key];
    if (Array.isArray(x?.rows)) return x.rows;
    if (Array.isArray(x?.items)) return x.items;
    if (Array.isArray(x?.data)) return x.data;
    return [];
  };

  const ts = () => ({ _ts: Date.now() });

  const isActiveVendor = (v) => {
    const s = (v?.status ?? "").toString().toLowerCase();
    if (s) return s === "active";
    if (typeof v?.is_active === "boolean") return v.is_active;
    if (v?.active != null) return !!v.active;
    return true;
  };

  const isValidId = (v) => {
    const n = Number(v);
    return Number.isInteger(n) && n > 0;
  };

  const pickId = (obj, keys = []) => {
    for (const k of keys) {
      if (obj?.[k] != null && obj?.[k] !== "") return obj[k];
    }
    return null;
  };

  const cleanText = (v) => {
    const s = String(v ?? "").trim();
    return s || null;
  };

  const vendorIdOf = (v) => {
    const raw = pickId(v, ["vendor_id", "vendorId", "id"]);
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const plannerIdOf = (p) => {
    const raw = pickId(p, ["planner_id", "plannerId", "id"]);
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const costCentreIdOf = (c) => {
    const raw = pickId(c, ["cost_centre_id", "costCentreId", "id"]);
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const eventIdOf = (ev) => {
    const raw = pickId(ev, ["event_id", "eventId", "id"]);
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const eventNameOf = (ev) => {
    return (
      cleanText(ev?.event_name) ||
      cleanText(ev?.eventName) ||
      cleanText(ev?.name) ||
      cleanText(ev?.title) ||
      null
    );
  };

  const eventLabelOf = (ev) => {
    const name =
      eventNameOf(ev) ||
      (eventIdOf(ev) ? `Event #${eventIdOf(ev)}` : "Unnamed Event");

    const date = ev?.event_date ?? ev?.date ?? ev?.expected_date ?? null;

    return date ? `${name} (${String(date).slice(0, 10)})` : name;
  };

  const itemCodeOf = (item) => {
    return String(item?.item_code ?? item?.itemCode ?? "").trim().toUpperCase();
  };

  const itemDescriptionOf = (item) => {
    return (
      cleanText(item?.description) ||
      cleanText(item?.item_name) ||
      cleanText(item?.itemName) ||
      cleanText(item?.name) ||
      itemCodeOf(item) ||
      "Unnamed item"
    );
  };

  const itemUnitSizeOf = (item) => {
    return cleanText(item?.unit_size) || cleanText(item?.unitSize) || "";
  };

  const getDraftPoIdFromQuery = () => {
    const qp = new URLSearchParams(search);

    const rawDraft =
      qp.get("draft_po_id") ||
      qp.get("draftPoId") ||
      qp.get("po_id") ||
      qp.get("poId");

    const mode = String(qp.get("mode") || qp.get("status") || "").toLowerCase();

    if (!isValidId(rawDraft)) return null;

    if (
      qp.has("draft_po_id") ||
      qp.has("draftPoId") ||
      mode === "draft" ||
      mode === "edit-draft" ||
      mode === "edit_draft"
    ) {
      return Number(rawDraft);
    }

    return null;
  };

  const parseEventPrefill = () => {
    const qp = new URLSearchParams(search);

    const rawEventId =
      qp.get("event_id") ?? qp.get("eventId") ?? qp.get("prefill_event_id");

    const rawEventName =
      qp.get("event_name") ?? qp.get("eventName") ?? qp.get("prefill_event_name");

    const event_id = isValidId(rawEventId) ? Number(rawEventId) : null;
    const event_name = rawEventName ? String(rawEventName).trim() : null;

    if (!event_id && !event_name) return null;

    return { event_id, event_name };
  };

  const decodeGroupsB64 = (b64) => {
    try {
      const json = decodeURIComponent(escape(atob(b64)));
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const decodeGroupB64 = (b64) => {
    try {
      const json = decodeURIComponent(escape(atob(b64)));
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };

  const findVendorById = (id, fallback = null) => {
    if (!isValidId(id)) return fallback;
    const match = vendors.find((v) => vendorIdOf(v) === Number(id));
    return match || fallback;
  };

  const findPlannerById = (id, fallback = null) => {
    if (!isValidId(id)) return fallback;
    const match = planners.find((p) => plannerIdOf(p) === Number(id));
    return match || fallback;
  };

  const findCostCentreById = (id, fallback = null) => {
    if (!isValidId(id)) return fallback;
    const match = costCentres.find((c) => costCentreIdOf(c) === Number(id));
    return match || fallback;
  };

  const findEventByIdOrName = (id, name, fallback = null) => {
    let match = null;

    if (isValidId(id)) {
      match = events.find((ev) => eventIdOf(ev) === Number(id)) || null;
    }

    if (!match && name) {
      match =
        events.find(
          (ev) =>
            String(eventNameOf(ev) || "").trim().toLowerCase() ===
            String(name).trim().toLowerCase()
        ) || null;
    }

    return match || fallback;
  };

  const getVendorOptionValue = (vendor) => {
    if (!vendor) return null;
    return {
      value: vendor,
      label: vendor.vendor_name ?? vendor.name ?? `Vendor #${vendorIdOf(vendor) ?? "?"}`,
    };
  };

  const getPlannerOptionValue = (planner) => {
    if (!planner) return null;
    return {
      value: planner,
      label:
        planner.planner_name ?? planner.name ?? `Planner #${plannerIdOf(planner) ?? "?"}`,
    };
  };

  const getCostCentreOptionValue = (costCentre) => {
    if (!costCentre) return null;
    return {
      value: costCentre,
      label:
        costCentre.name ??
        costCentre.cost_centre_name ??
        costCentre.centre_name ??
        `Cost Centre #${costCentreIdOf(costCentre) ?? "?"}`,
    };
  };

  const getEventOptionValue = (event) => {
    if (!event) return null;
    return {
      value: event,
      label: eventLabelOf(event),
    };
  };

  // ---------- fetchers ----------
  async function fetchVendorsMulti() {
    const tries = [
      () => api.get("/vendors", { params: { ...ts(), status: "active" } }),
      () => api.get("/vendors", { params: { ...ts(), status: "all" } }),
      () => api.get("/vendor-directory", { params: ts() }),
      () => api.get("/vendors/list", { params: ts() }),
    ];

    for (const t of tries) {
      try {
        const r = await t();
        return toArray(r?.data, "vendors");
      } catch {
        /* try next */
      }
    }

    throw new Error("No vendor endpoint responded");
  }

  async function fetchEventsMulti() {
    const tries = [
      () => api.get("/event", { params: ts() }),
      () => api.get("/event/events", { params: ts() }),
      () => api.get("/events", { params: ts() }),
      () => api.get("/event/list", { params: ts() }),
    ];

    for (const t of tries) {
      try {
        const r = await t();
        const arr = toArray(r?.data, "events");
        if (Array.isArray(arr)) return arr;
      } catch {
        /* try next */
      }
    }

    return [];
  }

  // ---------- bootstrap dropdowns ----------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBootLoading(true);

      try {
        const [v, p, c, e] = await Promise.allSettled([
          fetchVendorsMulti(),
          api.get("/planners", { params: ts() }),
          api.get("/cost-centres", { params: ts() }),
          fetchEventsMulti(),
        ]);

        if (cancelled) return;

        if (v.status === "fulfilled") {
          const raw = toArray(v.value);
          const uniq = Array.from(
            new Map(
              raw.map((r) => [r.vendor_id ?? r.id ?? r.vendor_name ?? r.name, r])
            ).values()
          );

          setVendors(uniq);

          if (uniq.length && !uniq.some(isActiveVendor) && !hasToastedRef.current) {
            toast.warn("All vendors are currently inactive. Activate some in Vendor Directory.");
            hasToastedRef.current = true;
          }
        }

        if (p.status === "fulfilled") {
          setPlanners(toArray(p.value?.data, "planners"));
        }

        if (c.status === "fulfilled") {
          setCostCentres(toArray(c.value?.data, "cost_centres"));
        }

        if (e.status === "fulfilled") {
          const eventRows = toArray(e.value, "events");
          const uniqEvents = Array.from(
            new Map(
              eventRows
                .filter((row) => eventIdOf(row) || eventNameOf(row))
                .map((row) => [eventIdOf(row) || `name:${eventNameOf(row)}`, row])
            ).values()
          );

          setEvents(uniqEvents);
        }

        if ([v, p, c].every((r) => r.status === "rejected") && !hasToastedRef.current) {
          toast.error("Could not load dropdown data");
          hasToastedRef.current = true;
        }
      } catch {
        if (!cancelled && !hasToastedRef.current) {
          toast.error("Could not load dropdown data");
          hasToastedRef.current = true;
        }
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- read query params for prefill ----------
  useEffect(() => {
    const qp = new URLSearchParams(search);

    const incomingDraftId = getDraftPoIdFromQuery();
    if (incomingDraftId) {
      setDraftPoId(incomingDraftId);
    }

    const b64Groups = qp.get("groups");
    const bulkFlag = qp.get("bulk_from_stock");
    const singleId = qp.get("prefill_item_id");
    const b64Group = qp.get("prefill_group");

    const eventPrefill = parseEventPrefill();

    if (eventPrefill) {
      setSelectedEvent((prev) => {
        if (prev) return prev;

        return {
          event_id: eventPrefill.event_id,
          event_name:
            eventPrefill.event_name ||
            (eventPrefill.event_id ? `Event #${eventPrefill.event_id}` : "Selected Event"),
        };
      });
    }

    if (incomingDraftId) return;

    if (b64Group && !prefillPlanRef.current) {
      const group = decodeGroupB64(b64Group);

      if (group && group.vendor_id && Array.isArray(group.items) && group.items.length) {
        prefillPlanRef.current = { type: "group", group };

        setSelectedVendor((prev) =>
          prev || {
            vendor_id: group.vendor_id,
            vendor_name: group.vendor_name || "Vendor",
          }
        );

        toast.info(`Prefill: loaded vendor group (${group.vendor_name || group.vendor_id}).`);
        return;
      }
    }

    if (b64Groups && bulkFlag && !prefillPlanRef.current) {
      const groups = decodeGroupsB64(b64Groups);

      if (groups.length) {
        prefillPlanRef.current = { type: "bulk", groups };

        const g = groups[0];

        if (g?.vendor_id) {
          setSelectedVendor((prev) =>
            prev || { vendor_id: g.vendor_id, vendor_name: g.vendor_name }
          );
        }

        toast.info(
          `Prefill: loaded ${groups.length} vendor group${
            groups.length > 1 ? "s" : ""
          }. Focusing first vendor.`
        );
        return;
      }
    }

    if (singleId && !prefillPlanRef.current) {
      const itemId = parseInt(singleId, 10);

      if (Number.isFinite(itemId)) {
        prefillPlanRef.current = { type: "single", item_id: itemId };

        (async () => {
          try {
            const { data } = await api.get(`/inventory/stock-items/${itemId}/vendors`, {
              params: ts(),
            });

            const list = toArray(data);

            if (list.length) {
              const v = list[0];
              setSelectedVendor({
                vendor_id: v.vendor_id,
                vendor_name: v.vendor_name,
              });
            } else {
              toast.warn("Selected item has no linked vendor; please choose a vendor.");
            }
          } catch {
            toast.warn("Could not resolve vendor for the selected item; please choose a vendor.");
          }
        })();
      }
    }
  }, [search]);

  // ---------- load existing draft ----------
  useEffect(() => {
    if (!draftPoId) return;
    if (draftLoadedRef.current) return;
    if (bootLoading) return;

    let cancelled = false;

    (async () => {
      setDraftLoading(true);

      try {
        const { data } = await api.get(`/purchase-orders/${draftPoId}/summary`, {
          params: ts(),
        });

        if (cancelled) return;

        const header = data?.header || {};

        if (String(header.po_id) !== String(draftPoId)) {
          throw new Error("The requested draft was not returned.");
        }
        setDraftStatus(header.status || null);
        if (header.status !== "Draft") {
          setDraftAccessError("This PO is no longer a draft. Editing and submission are disabled here.");
          draftLoadedRef.current = true;
          toast.warn(`PO ${header.po_number || draftPoId} is not a draft and cannot be edited here.`);
          return;
        }
        const financials = draftPoFinancials(data, financialContext.scope, draftPoId);
        setTransactionCurrency(financials.currency);
        setSavedFinancialHeader(financials.header);
        setQuoteConfirmed(true);

        setDraftPoId(header.po_id || draftPoId);
        setDraftPoNumber(header.po_number || null);
        setDraftStatus(header.status || "Draft");

        setDeliveryDate(header.expected_delivery ? String(header.expected_delivery).slice(0, 10) : "");
        setPoNotes(header.notes || "");
        setDraftAccessError("");

        const vendorFallback = header.vendor_id
          ? {
              vendor_id: header.vendor_id,
              vendor_name: header.vendor_name || `Vendor #${header.vendor_id}`,
            }
          : null;

        const plannerFallback = header.planner_id
          ? {
              planner_id: header.planner_id,
              planner_name: header.planner_name || `Planner #${header.planner_id}`,
            }
          : null;

        const costCentreFallback = header.cost_centre_id
          ? {
              cost_centre_id: header.cost_centre_id,
              name:
                header.cost_centre_name ||
                header.cost_centre_code ||
                `Cost Centre #${header.cost_centre_id}`,
            }
          : null;

        const eventFallback = header.event_id
          ? {
              event_id: header.event_id,
              event_name: header.event_name || `Event #${header.event_id}`,
              event_date: header.event_date || null,
            }
          : null;

        setSelectedVendor(findVendorById(header.vendor_id, vendorFallback));
        setSelectedPlanner(findPlannerById(header.planner_id, plannerFallback));
        setSelectedCostCentre(findCostCentreById(header.cost_centre_id, costCentreFallback));
        setSelectedEvent(findEventByIdOrName(header.event_id, header.event_name, eventFallback));

        setLineItems(financials.items);

        draftLoadedRef.current = true;
        prefillAppliedRef.current = true;

        toast.success(`Loaded draft ${header.po_number || `#${draftPoId}`}`);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err.message || "Could not load draft PO";
        setDraftAccessError(msg);
        toast.error(`❌ ${msg}`);
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draftPoId, bootLoading, vendors, planners, costCentres, events]);

  // ---------- align selected event with loaded event options ----------
  useEffect(() => {
    if (!selectedEvent) return;
    if (!events.length) return;

    const currentId = eventIdOf(selectedEvent);
    const currentName = eventNameOf(selectedEvent);

    let matched = null;

    if (currentId) {
      matched = events.find((ev) => eventIdOf(ev) === currentId) || null;
    }

    if (!matched && currentName) {
      matched =
        events.find(
          (ev) =>
            String(eventNameOf(ev) || "").trim().toLowerCase() ===
            currentName.trim().toLowerCase()
        ) || null;
    }

    if (matched) {
      const matchedId = eventIdOf(matched);
      const selectedId = eventIdOf(selectedEvent);
      const matchedName = eventNameOf(matched);
      const selectedName = eventNameOf(selectedEvent);

      if (matchedId !== selectedId || matchedName !== selectedName) {
        setSelectedEvent(matched);
      }
    }
  }, [events, selectedEvent]);

  // ---------- load items for selected vendor ----------
  useEffect(() => {
    if (!selectedVendor?.vendor_id && !selectedVendor?.id) {
      setVendorItems([]);
      return;
    }

    const vid = selectedVendor.vendor_id ?? selectedVendor.id;
    const abort = new AbortController();
    setVendorItems([]);

    api
      .get(`/inventory/stock-items/by-vendor/${vid}`, { params: ts(), signal: abort.signal })
      .then((res) => { if (!abort.signal.aborted) setVendorItems(toArray(res?.data, "items")); })
      .catch(() => {
        if (!abort.signal.aborted && !hasToastedRef.current) {
          toast.error("Could not load items for this vendor");
          hasToastedRef.current = true;
        }
      });
    return () => abort.abort();
  }, [selectedVendor]);

  // ---------- apply prefill once vendorItems are available ----------
  useEffect(() => {
    const plan = prefillPlanRef.current;

    if (!plan || prefillAppliedRef.current) return;

    const vid = selectedVendor?.vendor_id ?? selectedVendor?.id;
    if (!vid) return;
    if (!vendorItems.length) return;

    let cancelled = false;
    const makeLine = makeNewLine;

    const byId = new Map(vendorItems.map((it) => [String(it.item_id ?? it.id), it]));

    const byCode = new Map(
      vendorItems.map((it) => [String(it.item_code || "").toUpperCase(), it])
    );

    const addLinesFromList = (list, vendorLabel = "vendor") => {
      const found = [];
      const missing = [];

      for (const it of list || []) {
        const code = it.item_code ? String(it.item_code).toUpperCase() : null;
        const idStr =
          it.item_id != null ? String(it.item_id) : it.id != null ? String(it.id) : null;

        let item = null;

        if (code && byCode.has(code)) item = byCode.get(code);
        else if (idStr && byId.has(idStr)) item = byId.get(idStr);

        if (item) {
          const qty = Number(it.quantity);
          found.push(makeLine(item, Number.isFinite(qty) && qty > 0 ? qty : 1));
        } else {
          missing.push(code || idStr || "?");
        }
      }

      if (found.length) {
        setQuoteConfirmed(false);
        setLineItems((prev) => {
          const existingCodes = new Set(prev.map((p) => itemCodeOf(p)));
          const add = found.filter((f) => !existingCodes.has(itemCodeOf(f)));
          return [...prev, ...add];
        });

        toast.success(
          `Prefilled ${found.length} item${found.length > 1 ? "s" : ""} for ${vendorLabel}.`
        );
      } else {
        toast.warn("No matching items were found for this vendor in the selection.");
      }

      if (missing.length) {
        toast.info(
          `Some items not found for this vendor (${missing.length}). Check vendor links or item codes.`
        );
      }
    };

    if (plan.type === "group") {
      const g = plan.group;
      if (String(g.vendor_id) !== String(vid)) return;

      addLinesFromList(g.items, g.vendor_name || "vendor");
      prefillAppliedRef.current = true;
    } else if (plan.type === "bulk") {
      const group =
        plan.groups.find((g) => String(g.vendor_id) === String(vid)) || plan.groups[0];

      if (group) {
        addLinesFromList(group.items, group.vendor_name || "vendor");
        prefillAppliedRef.current = true;
      }
    } else if (plan.type === "single") {
      const key = String(plan.item_id);
      const item = byId.get(key);

      if (item) {
        setQuoteConfirmed(false);
        setLineItems((prev) => {
          if (prev.some((p) => itemCodeOf(p) === itemCodeOf(item))) return prev;
          return [...prev, makeLine(item, 1)];
        });

        toast.success("Prefilled 1 item from Stock Levels.");
      } else {
        (async () => {
          try {
            const { data } = await api.get(`/inventory/stock-items/${plan.item_id}`, {
              params: ts(),
            });
            if (cancelled) return;

            const it = data || {};
            if (!it.item_code) return toast.warn("Could not prefill that item.");

            const code = String(it.item_code).toUpperCase();
            const local = byCode.get(code) || it;

            setLineItems((prev) => {
              if (prev.some((p) => itemCodeOf(p) === itemCodeOf(local))) return prev;

              return [
                ...prev,
                makeLine(local),
              ];
            });

            setQuoteConfirmed(false);
            toast.success("Prefilled 1 item from Stock Levels.");
          } catch {
            if (!cancelled) toast.warn("Could not prefill that item.");
          }
        })();
      }

      prefillAppliedRef.current = true;
    }
    return () => { cancelled = true; };
  }, [selectedVendor, vendorItems]);

  // ---------- handlers ----------
  const handleVendorChange = (opt) => {
    if (lineItems.length > 0) {
      toast.error("Remove all items before changing vendor.");
      return;
    }

    setSelectedVendor(opt?.value || null);
    setQuoteConfirmed(false);
    prefillAppliedRef.current = false;
  };

  const handleAddItem = (item) => {
    if (!item) return;

    if (lineItems.some((li) => itemCodeOf(li) === itemCodeOf(item))) {
      return toast.info("Item already added");
    }

    setLineItems((prev) => [...prev, makeNewLine(item)]);
    setQuoteConfirmed(false);
  };

  const handleItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const copy = [...prev];

      if (["quantity", "unit_price", "tax_rate"].includes(field)) {
        copy[index] = {
          ...copy[index],
          [field]: value,
        };
      }

      return copy;
    });
    if (field === "unit_price") setQuoteConfirmed(false);
  };

  const handleDeleteItem = (index) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const resetFormAfterCreate = () => {
    setDraftPoId(null);
    setDraftPoNumber(null);
    setDraftStatus(null);
    setPoNotes("");
    setDraftAccessError("");
    setSavedFinancialHeader(null);
    setFxEditing(false);
    setFxFields({rate_to_unit:'',rate_to_reporting:'',source_reference:'',as_of_date:''});
    setTransactionCurrency(financialContext.defaults.transaction_currency || '');
    setQuoteConfirmed(false);

    setSelectedVendor(null);
    setDeliveryDate("");
    setLineItems([]);
    setVendorItems([]);
    setSelectedPlanner(null);
    setSelectedCostCentre(null);
    setSelectedEvent(null);

    prefillPlanRef.current = null;
    prefillAppliedRef.current = false;
    draftLoadedRef.current = false;
  };

  // ---------- totals ----------
  const financialPreview = useMemo(() => {
    try { return { totals: assertPoMoneyRange(previewPoTotals(lineItems)), error: '' }; }
    catch (cause) { return { totals: null, error: cause.message }; }
  }, [lineItems]);
  const currencyReady = isPoCurrencySupported(transactionCurrency) &&
    isPoCurrencySupported(financialContext.unit_currency) && isPoCurrencySupported(financialContext.reporting_currency);
  const fxEntry = useMemo(() => {
    if (!fxEditing) return {value:null,error:''};
    try { return {value:poManualFxEntry(financialContext,transactionCurrency,fxFields,savedFinancialHeader),error:''}; }
    catch (cause) { return {value:null,error:cause.message}; }
  }, [fxEditing,financialContext,transactionCurrency,fxFields,savedFinancialHeader]);
  const fxHeader = fxEntry.value ? {transaction_currency:transactionCurrency,unit_currency:financialContext.unit_currency,
    reporting_currency:financialContext.reporting_currency,fx_rate_to_unit_exact:fxEntry.value.rate_to_unit,
    fx_rate_to_reporting_exact:fxEntry.value.rate_to_reporting,fx_rate_source:'MANUAL:'+fxEntry.value.source_reference,
    fx_rate_timestamp:fxEntry.value.as_of_date+'T00:00:00.000Z'} : savedFinancialHeader;
  const fxReady = !fxEntry.error && poFxReady(financialContext, transactionCurrency, fxHeader);
  const convertedPreview = useMemo(() => {
    try {
      const totals={};
      for (const prefix of ['unit','reporting']) {
        const target=financialContext[prefix+'_currency'];
        const rate=transactionCurrency===target?'1':fxReady?fxHeader?.['fx_rate_to_'+prefix+'_exact'] ?? fxHeader?.['fx_rate_to_'+prefix]:null;
        totals[prefix]=rate==null?null:assertPoMoneyRange(previewPoTotals(lineItems,String(rate)));
      }
      return {totals,error:''};
    } catch(cause) { return {totals:null,error:cause.message}; }
  }, [financialContext,transactionCurrency,fxReady,fxHeader,lineItems]);

  // ---------- payload ----------
  const buildPayload = ({ status }) => {
    const vendor_id = selectedVendor ? vendorIdOf(selectedVendor) : null;
    const planner_id = selectedPlanner ? plannerIdOf(selectedPlanner) : null;
    const cost_centre_id = selectedCostCentre ? costCentreIdOf(selectedCostCentre) : null;
    const event_id = selectedEvent ? eventIdOf(selectedEvent) : null;
    const event_name = selectedEvent ? eventNameOf(selectedEvent) : null;

    const linePayload = lineItems.map(serializePoLine);

    const basePayload = {
      status,
      save_as_draft: status === "Draft",
      is_draft: status === "Draft",
      vendor_id,
      planner_id,
      cost_centre_id,
      expected_delivery: deliveryDate || null,
      notes: poNotes,
      lineItems: linePayload,
      transaction_currency: transactionCurrency,
      expected_context: financialContext.scope,
      ...(financialContext.current_rules.manual_fx_entry === true ? {expected_currencies:{unit_currency:financialContext.unit_currency,reporting_currency:financialContext.reporting_currency}} : {}),
      ...(fxEntry.value ? {fx_entry:fxEntry.value} : {}),
    };

    const eventPayload = {
      ...(event_id ? { event_id, eventId: event_id } : {}),
      ...(event_name ? { event_name, eventName: event_name } : {}),
    };

    const payloadWithEvent =
      event_id || event_name ? { ...basePayload, ...eventPayload } : basePayload;

    const createdBy = user?.id ?? user?.user_id;

    return createdBy != null
      ? { ...payloadWithEvent, created_by: createdBy }
      : payloadWithEvent;
  };

  // ---------- validation ----------
  const submissionMissing = [
    !selectedVendor && 'vendor', !deliveryDate && 'delivery date',
    !selectedPlanner && 'planner', !selectedCostCentre && 'cost centre',
    lineItems.length === 0 && 'at least one item',
  ].filter(Boolean);
  const submitIncomplete = submissionMissing.length > 0;

  const draftHasItemsButNoVendor = lineItems.length > 0 && !selectedVendor;

  const inputsBusy = savingDraft || submitting || Boolean(attachments.phase) ||
    Boolean(attachments.paused) || Boolean(draftAccessError) ||
    Boolean(draftPoId && !draftLoadedRef.current) ||
    Boolean(draftStatus && draftStatus !== "Draft");

  const financialIncomplete = !currencyReady || Boolean(financialPreview.error) || Boolean(fxEntry.error) || Boolean(convertedPreview.error) || (lineItems.length > 0 && !quoteConfirmed);
  const draftCannotSave = inputsBusy || draftHasItemsButNoVendor || financialIncomplete;
  const submitCannotRun = inputsBusy || submitIncomplete || financialIncomplete || !fxReady;

  // Single coordinator: save draft -> upload sequentially -> verify links -> submit.
  // The no-attachment direct-create path keeps the existing Pending behaviour.
  const performSave = async (intent) => {
    if (actionLock.current || inputsBusy) return;
    if (financialIncomplete || (intent === 'submit' && !fxReady)) return;
    if (draftHasItemsButNoVendor) {
      toast.error("Select a vendor before saving draft items.");
      return;
    }
    if (intent === "submit" && submitIncomplete) {
      toast.error("Please complete all required fields and add at least one item.");
      return;
    }
    actionLock.current = true;
    if (intent === "draft") setSavingDraft(true); else setSubmitting(true);
    try {
      const result = await attachments.run({ intent, payload: buildPayload({ status: "Draft" }) });
      if (!result) return;
      const saved = result.po;
      if (result.submitted) {
        toast.success(`PO ${saved.po_number || saved.po_id} submitted successfully.`);
        resetFormAfterCreate();
        // Remove a completed draft's query so refresh cannot resubmit/reopen it as a draft.
        navigate({ pathname, search: "" }, { replace: true });
      } else {
        toast.success(`Draft ${saved.po_number || saved.po_id} and selected attachments saved.`);
        // URL changes only AFTER all selected files are saved and re-read successfully.
        // A failed upload leaves this mounted form, draft ID and pending queue intact.
        navigate({ pathname, search: `?draft_po_id=${saved.po_id}` }, { replace: true });
      }
    } catch (cause) {
      toast.error(cause.message || 'The PO financial details could not be verified.');
    } finally {
      actionLock.current = false;
      setSavingDraft(false);
      setSubmitting(false);
    }
  };
  const handleSaveDraft = () => performSave("draft");
  const handleSubmit = () => performSave("submit");

  // ---------- options ----------
  const activeVendors = vendors.filter(isActiveVendor);

  const vendorOptions = useMemo(
    () =>
      activeVendors.map((v) => ({
        value: v,
        label: v.vendor_name ?? v.name ?? `Vendor #${vendorIdOf(v) ?? "?"}`,
      })),
    [activeVendors]
  );

  const plannerOptions = useMemo(
    () =>
      planners.map((p) => ({
        value: p,
        label: p.planner_name ?? p.name ?? `Planner #${plannerIdOf(p) ?? "?"}`,
      })),
    [planners]
  );

  const costCentreOptions = useMemo(
    () =>
      costCentres.map((c) => ({
        value: c,
        label:
          c.name ??
          c.cost_centre_name ??
          c.centre_name ??
          `Cost Centre #${costCentreIdOf(c) ?? "?"}`,
      })),
    [costCentres]
  );

  const eventOptions = useMemo(
    () =>
      events.map((ev) => ({
        value: ev,
        label: eventLabelOf(ev),
      })),
    [events]
  );

  const defaultItemOptions = useMemo(
    () =>
      vendorItems.map((item) => ({
        value: item,
        label: `${item.item_code} - ${itemDescriptionOf(item)}`,
      })),
    [vendorItems]
  );

  const pageTitle = draftPoId
    ? `Edit Draft Purchase Order${draftPoNumber ? ` — ${draftPoNumber}` : ""}`
    : "Create New Purchase Order";

  const pageSubtitle = draftPoId
    ? "This PO is still a draft. You can save changes or submit it for approval."
    : "Create a PO or save a flexible draft. Supporting files are saved before an attachment-bearing PO is submitted.";

  // ---------- UI ----------
  return (
    <div className="bg-blue-900 text-white p-6 rounded-xl shadow-xl w-full max-w-5xl mx-auto">
      <ToastContainer />

      <div className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">{pageTitle}</h2>
          <p className="text-sm text-blue-100 mt-1">{pageSubtitle}</p>
        </div>

        {draftPoId && (
          <div className="rounded-lg bg-blue-800/80 border border-blue-500/40 px-4 py-2 text-sm">
            <div className="text-blue-100">Draft Status</div>
            <div className="font-bold">
              {draftStatus || "Draft"} {draftPoNumber ? `• ${draftPoNumber}` : ""}
            </div>
          </div>
        )}
      </div>

      {draftAccessError && <div role="alert" className="mb-4 rounded-lg bg-red-950/60 p-4 text-sm">{draftAccessError} Reopen the PO from its current list before editing.</div>}
      {attachments.phase && <p role="status" className="mb-4 rounded-lg bg-blue-800 p-3 text-sm">Working: {attachments.phase.replaceAll("-", " ")}. Please keep this page open.</p>}

      {bootLoading || draftLoading ? (
        <div className="p-4 text-sm text-slate-200">
          {draftLoading ? "Loading draft purchase order…" : "Loading form data…"}
        </div>
      ) : (
        <>
          <fieldset disabled={inputsBusy} className="min-w-0 border-0 p-0">
          <div className="mb-6 rounded-lg border border-blue-400/50 bg-blue-950/40 p-4">
            <h3 className="font-semibold">Currency and expected tax</h3>
            <p className="mt-1 text-sm text-blue-100">
              {financialContext.unit_name || 'Selected unit'} · Unit: {financialContext.unit_currency || 'Not configured'} · Company reporting: {financialContext.reporting_currency || 'Not configured'}
            </p>
            <label htmlFor="po-transaction-currency" className="mt-3 block text-sm font-semibold">PO transaction currency</label>
            <select id="po-transaction-currency" value={transactionCurrency}
              onChange={event => { setTransactionCurrency(event.target.value); setQuoteConfirmed(false); setFxEditing(false);
                setFxFields({rate_to_unit:'',rate_to_reporting:'',source_reference:'',as_of_date:''}); }}
              className="mt-1 w-full rounded-lg border p-2 text-black md:w-64">
              <option value="">Choose currency</option>
              {transactionCurrency && !PO_CURRENCIES.includes(transactionCurrency) && <option value={transactionCurrency}>{transactionCurrency} — unavailable</option>}
              {PO_CURRENCIES.map(code => <option key={code} value={code}>{code}</option>)}
            </select>
            <p className="mt-2 text-sm text-blue-100">Enter the supplier’s quoted prices before tax in this currency. Changing the currency does not convert prices.</p>
            <p className="mt-1 text-sm text-blue-100">New lines start with the unit’s tax default: {financialContext.default_tax_rate === null ? 'not configured — enter each line rate' : `${financialContext.default_tax_rate}%`}. Review each rate against the supplier’s quote.</p>
            {!currencyReady && <p role="alert" className="mt-2 text-amber-200">Configure the unit and company currencies and choose a supported PO currency before saving. This form currently supports currencies with two decimal places.</p>}
            {currencyReady && !fxReady && <p role="status" className="mt-2 text-amber-200">Exchange rates to the unit or reporting currency are still required. You can save a draft; submission becomes available when valid rates have been recorded for these currencies.</p>}
            <POExchangeRates context={financialContext} currency={transactionCurrency} saved={savedFinancialHeader}
              canEdit={hasPermission('procurement.po.edit')} editing={fxEditing} fields={fxFields} error={fxEntry.error} busy={inputsBusy}
              onChange={(key,value)=>setFxFields(previous=>({...previous,[key]:value}))}
              onCancel={()=>setFxEditing(false)} onEdit={()=>{
                const matching=savedFinancialHeader?.transaction_currency===transactionCurrency &&
                  savedFinancialHeader.unit_currency===financialContext.unit_currency && savedFinancialHeader.reporting_currency===financialContext.reporting_currency;
                setFxFields({rate_to_unit:matching?savedFinancialHeader.fx_rate_to_unit_exact || '':'',
                  rate_to_reporting:matching?savedFinancialHeader.fx_rate_to_reporting_exact || '':'',
                  source_reference:matching?String(savedFinancialHeader.fx_rate_source || '').replace(/^MANUAL:/,''):'',
                  as_of_date:matching?String(savedFinancialHeader.fx_rate_timestamp || '').slice(0,10):''});
                setFxEditing(true);
              }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block font-semibold mb-1">
                Vendor{" "}
                <span className="text-slate-300 text-sm">
                  {lineItems.length ? "(required because items exist)" : "(optional for draft)"}
                </span>
              </label>

              <div className="flex gap-2">
                <Select
                  isDisabled={inputsBusy}
                  options={vendorOptions}
                  onChange={handleVendorChange}
                  value={getVendorOptionValue(selectedVendor)}
                  isClearable
                  placeholder={
                    activeVendors.length
                      ? "Select vendor..."
                      : vendors.length
                        ? "All vendors are inactive — activate some in Vendor Directory"
                        : "No vendors found — add some in Vendor Directory"
                  }
                  className="text-black flex-1"
                />

                <button
                  type="button"
                  title="Refresh vendors"
                  className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  onClick={async () => {
                    setBootLoading(true);

                    try {
                      const raw = await fetchVendorsMulti();

                      const uniq = Array.from(
                        new Map(
                          raw.map((x) => [
                            x.vendor_id ?? x.id ?? x.vendor_name ?? x.name,
                            x,
                          ])
                        ).values()
                      );

                      setVendors(uniq);
                      toast.success("Vendors refreshed");

                      if (uniq.length && !uniq.some(isActiveVendor)) {
                        toast.warn("All vendors are inactive. Activate them in Vendor Directory.");
                      }
                    } catch {
                      toast.error("Could not refresh vendors");
                    } finally {
                      setBootLoading(false);
                    }
                  }}
                >
                  Refresh
                </button>
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-1">
                Delivery Date{" "}
                <span className="text-slate-300 text-sm">(required for submit)</span>
              </label>

              <input
                type="date"
                value={deliveryDate}
                min={todayISO}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full p-2 border rounded-lg text-black"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">
                Planner <span className="text-slate-300 text-sm">(required for submit)</span>
              </label>

              <Select
                isDisabled={inputsBusy}
                options={plannerOptions}
                onChange={(opt) => setSelectedPlanner(opt?.value || null)}
                value={getPlannerOptionValue(selectedPlanner)}
                isClearable
                placeholder={planners.length ? "Select planner..." : "No planners found"}
                className="text-black"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">
                Cost Centre <span className="text-slate-300 text-sm">(required for submit)</span>
              </label>

              <Select
                isDisabled={inputsBusy}
                options={costCentreOptions}
                onChange={(opt) => setSelectedCostCentre(opt?.value || null)}
                value={getCostCentreOptionValue(selectedCostCentre)}
                isClearable
                placeholder={costCentres.length ? "Select cost centre..." : "No cost centres found"}
                className="text-black"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block font-semibold mb-1">
                Event <span className="text-slate-300 text-sm">(optional)</span>
              </label>

              <Select
                isDisabled={inputsBusy}
                options={eventOptions}
                onChange={(opt) => setSelectedEvent(opt?.value || null)}
                value={getEventOptionValue(selectedEvent)}
                isClearable
                placeholder={
                  events.length
                    ? "Link this PO to an event (optional)..."
                    : "No events found"
                }
                className="text-black"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block font-semibold mb-1">Search & Add Item:</label>

            <AsyncSelect
              isDisabled={inputsBusy || !selectedVendor}
              cacheOptions
              defaultOptions={defaultItemOptions}
              loadOptions={(inputValue) =>
                Promise.resolve(
                  vendorItems
                    .filter((i) => {
                      const q = (inputValue || "").toLowerCase();

                      return (
                        i.item_code?.toLowerCase().includes(q) ||
                        itemDescriptionOf(i).toLowerCase().includes(q)
                      );
                    })
                    .map((item) => ({
                      value: item,
                      label: `${item.item_code} - ${itemDescriptionOf(item)}`,
                    }))
                )
              }
              onChange={(opt) => handleAddItem(opt?.value)}
              placeholder={selectedVendor ? "Search items..." : "Select vendor first"}
              className="text-black"
              isClearable
            />

            {!selectedVendor && (
              <p className="text-xs text-blue-100 mt-1">
                You can save an empty/header-only draft now. Select a vendor when you are ready to add items.
              </p>
            )}
          </div>

          {lineItems.length > 0 && (
            <div className="space-y-2 mt-6">
              {lineItems.map((item, idx) => (
                <div
                  key={`${itemCodeOf(item)}-${idx}`}
                  className="grid grid-cols-1 md:grid-cols-8 gap-3 items-end bg-blue-800 p-3 rounded"
                >
                  <div className="md:col-span-2 font-medium">
                    <div>{itemDescriptionOf(item)}</div>
                    <div className="text-xs text-blue-200">{itemCodeOf(item)}</div>
                  </div>

                  <div className="text-sm">{itemUnitSizeOf(item)}</div>

                  <label className="text-xs">Quantity
                  <input
                    aria-label={`Quantity for ${itemDescriptionOf(item)}`}
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                    className="mt-1 w-full p-2 border rounded text-black"
                    min="1" step="1"
                  />
                  </label>

                  <label className="text-xs">Unit price ({transactionCurrency || 'currency'})
                  <input
                    aria-label={`Unit price for ${itemDescriptionOf(item)}`}
                    type="number"
                    value={item.unit_price}
                    onChange={event => handleItemChange(idx, 'unit_price', event.target.value)}
                    min="0.000001" step="0.000001"
                    className="mt-1 w-full p-2 border rounded text-black"
                  />
                  </label>

                  <label className="text-xs">Tax (%)
                    <input aria-label={`Tax percentage for ${itemDescriptionOf(item)}`} type="number"
                      value={item.tax_rate} onChange={event => handleItemChange(idx, 'tax_rate', event.target.value)}
                      min="0" max="100" step="0.0001" placeholder="Enter rate"
                      className="mt-1 w-full p-2 border rounded text-black" />
                  </label>

                  <div className="text-right text-sm font-semibold">
                    <div className="text-xs font-normal text-blue-100">Line before tax</div>
                    {(() => { try { return fmt(previewPoTotals([item]).net); } catch { return 'Check values'; } })()}
                  </div>

                  <button
                    onClick={() => handleDeleteItem(idx)}
                    className="text-red-400 hover:text-red-600"
                    type="button"
                  >
                    ✖ Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {lineItems.length > 0 && <label className="mt-4 flex items-start gap-3 rounded-lg bg-blue-950/40 p-3 text-sm">
            <input type="checkbox" className="mt-0.5" checked={quoteConfirmed} onChange={event => setQuoteConfirmed(event.target.checked)} />
            <span>I have checked that all unit prices are before tax and quoted in {transactionCurrency || 'the selected currency'}.</span>
          </label>}
          {financialPreview.error && <p role="alert" className="mt-3 text-amber-200">{financialPreview.error}</p>}
          {convertedPreview.error && <p role="alert" className="mt-3 text-amber-200">{convertedPreview.error}</p>}

          <div className="border-t pt-4 text-right mt-6 space-y-1">
            <div>Subtotal: {financialPreview.totals ? fmt(financialPreview.totals.net) : 'Check values'}</div>
            <div>Expected tax: {financialPreview.totals ? fmt(financialPreview.totals.tax) : 'Check values'}</div>
            {financialPreview.totals && financialPreview.totals.rounding !== '0.00' && <div>Rounding: {fmt(financialPreview.totals.rounding)}</div>}
            <div className="text-lg font-bold">Grand Total: {financialPreview.totals ? fmt(financialPreview.totals.gross) : 'Check values'}</div>
            {currencyReady && [financialContext.unit_currency,financialContext.reporting_currency].some(code=>code!==transactionCurrency) && <div className="pt-3 text-sm text-blue-100">
              {['unit','reporting'].map(prefix=><div key={prefix}>{prefix==='unit'?'Unit total':'Company reporting total'}: {convertedPreview.totals?.[prefix] ? formatPoMoney(convertedPreview.totals[prefix].gross,financialContext[prefix+'_currency'],financialContext.locale) : 'Rate required'}</div>)}
            </div>}
          </div>

          </fieldset>

          <POAttachmentPanel
            workflow={attachments}
            draftId={draftPoId}
            status={draftStatus}
            canView={canViewDocuments}
            canUpload={canUploadDocuments}
            hasCostCentre={Boolean(selectedCostCentre && costCentreIdOf(selectedCostCentre))}
            busy={savingDraft || submitting || Boolean(attachments.phase)}
          />

          {draftHasItemsButNoVendor && (
            <div className="mt-4 rounded-lg bg-red-900/50 border border-red-400/50 p-3 text-sm">
              Select a vendor before saving item lines. Empty drafts can be saved without a vendor,
              but item drafts must belong to a vendor.
            </div>
          )}

          {lineItems.length > 0 && submissionMissing.length > 0 && <p role="status" className="mt-4 text-sm text-amber-200">
            To submit, complete: {submissionMissing.join(', ')}.
          </p>}

          <div className="mt-6 flex flex-col md:flex-row justify-center gap-3">
            <button
              onClick={handleSaveDraft}
              disabled={draftCannotSave}
              title={
                draftHasItemsButNoVendor
                  ? "Select a vendor before saving draft items"
                  : "Save this PO as a draft"
              }
              className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-2 px-8 rounded disabled:opacity-50"
              type="button"
            >
              {savingDraft ? "Saving Draft…" : draftPoId ? "Save Draft Changes" : "Save Draft"}
            </button>

            <button
              onClick={handleSubmit}
              disabled={submitCannotRun}
              title={
                submitIncomplete
                  ? "Complete all required fields and add items"
                  : "Submit PO for approval"
              }
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-8 rounded disabled:opacity-50"
              type="button"
            >
              {submitting ? "Submitting…" : draftPoId ? "Submit Draft PO" : "Submit Purchase Order"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
